import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

export interface BoardPost {
  projectCode: string;
  boardCode: string;
  categoryCode?: string;
  code: string;
  status: string;
  sortOrder?: string;
  depth?: number;
  pin?: number;
  title: string;
  content?: string;
  viewCount: number;
  commentCount: number;
  likeCount: number;
  dislikeCount: number;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface GetPostListParams {
  cursor?: string;
  size?: number;
  sinceToken?: string;
  categoryCode?: string;
  status?: string;
  keyword?: string;
}

export interface PostListResult {
  posts: BoardPost[];
  nextCursor?: string;
  hasNext: boolean;
  newPostCount?: number;
}

interface ApiErrorBody {
  timestamp?: number;
  errors?: {
    error?: Array<{ code: number; target: string; message: string }>;
  };
  message?: string;
  data?: unknown;
}

function getAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function postsBasePath(projectCode: string, boardCode: string): string {
  return `/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}/posts`;
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const result: ApiErrorBody = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return result as T;
}

function normalizePost(raw: Record<string, unknown>): BoardPost {
  return {
    projectCode: String(raw.projectCode ?? ''),
    boardCode: String(raw.boardCode ?? ''),
    categoryCode:
      raw.categoryCode != null && String(raw.categoryCode).trim() !== ''
        ? String(raw.categoryCode)
        : undefined,
    code: String(raw.code ?? ''),
    status: String(raw.status ?? ''),
    sortOrder: raw.sortOrder != null ? String(raw.sortOrder) : undefined,
    depth: raw.depth != null ? Number(raw.depth) : undefined,
    pin: raw.pin != null ? Number(raw.pin) : undefined,
    title: String(raw.title ?? ''),
    content: typeof raw.content === 'string' ? raw.content : undefined,
    viewCount: Number(raw.viewCount ?? raw.view_count ?? 0) || 0,
    commentCount: Number(raw.commentCount ?? raw.comment_count ?? 0) || 0,
    likeCount: Number(raw.likeCount ?? raw.like_count ?? 0) || 0,
    dislikeCount: Number(raw.dislikeCount ?? raw.dislike_count ?? 0) || 0,
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
    updatedAt:
      typeof raw.updatedAt === 'string'
        ? raw.updatedAt
        : typeof raw.updated_at === 'string'
          ? raw.updated_at
          : undefined,
  };
}

function extractPostList(data: unknown): PostListResult {
  if (!data || typeof data !== 'object') {
    return { posts: [], hasNext: false };
  }
  const obj = data as Record<string, unknown>;
  const list = obj.posts ?? obj.items ?? obj.list;
  const posts = Array.isArray(list)
    ? list.map((item) => normalizePost(item as Record<string, unknown>))
    : [];
  return {
    posts,
    nextCursor: typeof obj.nextCursor === 'string' ? obj.nextCursor : undefined,
    hasNext: Boolean(obj.hasNext),
    newPostCount:
      obj.newPostCount != null ? Number(obj.newPostCount) : undefined,
  };
}

export interface BoardPostCreateRequest {
  parentCode?: string;
  categoryCode?: string;
  title: string;
  content: string;
}

export function getPostCreatePath(projectCode: string, boardCode: string): string {
  return `/projects/${projectCode}/posts/${encodeURIComponent(boardCode)}/create`;
}

export function getPostViewPath(
  projectCode: string,
  boardCode: string,
  postCode: string
): string {
  return `/projects/${projectCode}/posts/${encodeURIComponent(boardCode)}/${encodeURIComponent(postCode)}`;
}

export function getPostEditPath(
  projectCode: string,
  boardCode: string,
  postCode: string
): string {
  return `/projects/${projectCode}/posts/${encodeURIComponent(boardCode)}/${encodeURIComponent(postCode)}/edit`;
}

export function getPostReplyPath(
  projectCode: string,
  boardCode: string,
  parentCode: string
): string {
  const base = getPostCreatePath(projectCode, boardCode);
  return `${base}?parentCode=${encodeURIComponent(parentCode)}`;
}

export interface BoardPostUpdateRequest {
  title: string;
  content: string;
}

export interface PostThreadResult {
  posts: BoardPost[];
}

/** 스레드에서 원글(depth 0) 찾기 */
export function findThreadRoot(
  posts: BoardPost[],
  current?: BoardPost | null
): BoardPost | null {
  const fromThread = posts.find((p) => (p.depth ?? 0) === 0);
  if (fromThread) return fromThread;
  if (current && (current.depth ?? 0) === 0) return current;
  if (posts.length === 0) return current ?? null;
  return posts.reduce((min, p) =>
    (p.depth ?? 0) < (min.depth ?? 0) ? p : min
  );
}

/**
 * 게시물 상세
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}/posts/{postCode}
 */
export const getPost = async (
  projectCode: string,
  boardCode: string,
  postCode: string
): Promise<BoardPost> => {
  const url = getApiV1Url(
    `${postsBasePath(projectCode, boardCode)}/${encodeURIComponent(postCode)}`
  );
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizePost(raw as Record<string, unknown>);
  }
  throw new Error('게시물 상세 응답을 처리할 수 없습니다.');
};

/**
 * 스레드 전체(원글 + 답글)
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}/posts/thread/{postCode}
 */
export const getPostThread = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  activeOnly = false
): Promise<PostThreadResult> => {
  const qs = new URLSearchParams({ activeOnly: String(activeOnly) });
  const url = getApiV1Url(
    `${postsBasePath(projectCode, boardCode)}/thread/${encodeURIComponent(postCode)}?${qs}`
  );
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  return extractPostList(result.data);
};

/**
 * 게시물 수정
 * POST /api/v1/projects/{projectCode}/boards/{boardCode}/posts/{postCode}
 */
export const updatePost = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  body: BoardPostUpdateRequest
): Promise<BoardPost> => {
  const url = getApiV1Url(
    `${postsBasePath(projectCode, boardCode)}/${encodeURIComponent(postCode)}`
  );
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      title: body.title.trim(),
      content: body.content,
    }),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizePost(raw as Record<string, unknown>);
  }
  throw new Error('게시물 수정 응답을 처리할 수 없습니다.');
};

function getAuthHeadersMultipart(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function buildCreatePayload(body: BoardPostCreateRequest): BoardPostCreateRequest {
  return {
    title: body.title.trim(),
    content: body.content,
    ...(body.parentCode?.trim() ? { parentCode: body.parentCode.trim() } : {}),
    ...(body.categoryCode?.trim() ? { categoryCode: body.categoryCode.trim() } : {}),
  };
}

/**
 * 게시물 생성
 * POST /api/v1/projects/{projectCode}/boards/{boardCode}/posts
 * - 첨부 파일이 있으면 multipart/form-data (request JSON + files)
 * - 없으면 application/json
 */
export const createPost = async (
  projectCode: string,
  boardCode: string,
  body: BoardPostCreateRequest,
  files?: File[]
): Promise<BoardPost> => {
  const url = getApiV1Url(postsBasePath(projectCode, boardCode));
  const hasFiles = files != null && files.length > 0;

  let response: Response;

  if (hasFiles) {
    const formData = new FormData();
    const jsonPayload = buildCreatePayload(body);
    formData.append(
      'request',
      new Blob([JSON.stringify(jsonPayload)], { type: 'application/json' })
    );
    files!.forEach((file) => formData.append('files', file));

    response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeadersMultipart(),
      body: formData,
    });
  } else {
    response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(buildCreatePayload(body)),
    });
  }

  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizePost(raw as Record<string, unknown>);
  }
  throw new Error('게시물 생성 응답을 처리할 수 없습니다.');
};

/**
 * 게시물 목록 조회 (커서 페이지)
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}/posts
 */
export const getPostList = async (
  projectCode: string,
  boardCode: string,
  params?: GetPostListParams
): Promise<PostListResult> => {
  const queryParams = new URLSearchParams();
  if (params?.cursor) queryParams.append('cursor', params.cursor);
  if (params?.size != null) queryParams.append('size', String(params.size));
  if (params?.sinceToken) queryParams.append('sinceToken', params.sinceToken);
  if (params?.categoryCode) queryParams.append('categoryCode', params.categoryCode);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.keyword) queryParams.append('keyword', params.keyword);

  const qs = queryParams.toString();
  const url = getApiV1Url(`${postsBasePath(projectCode, boardCode)}${qs ? `?${qs}` : ''}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const result = await parseApiResponse<ApiErrorBody>(response);
  return extractPostList(result.data);
};
